// =============================================================================
// HOOK USECHARTDATA - Données graphiques pour un chantier
// Migré vers React Query (useQuery + invalidation)
// =============================================================================

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, subDays, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { getMargeColor, getBudgetColor, CHART_STATUS_COLORS, FRAIS_TYPE_COLORS } from '@/components/charts/chartConfig';
import { SEUILS_MARGE } from '@/lib/calculs-rentabilite';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { queryKeys } from '@/lib/queryKeys';

// =============================================================================
// TYPES
// =============================================================================

export interface EvolutionDataPoint {
  date: string;
  dateLabel: string;
  marge: number;
  coutsCumules: number;
  budget: number;
}

export interface RentabiliteData {
  value: number;           // Rentabilité temps réel (à ce jour)
  valueFinale: number;     // Rentabilité finale estimée
  objectif: number;
  color: string;
  colorFinale: string;
  status: string;
  statusFinale: string;
  hasActivity: boolean; // True si le chantier a des dépenses ou affectations
}

export interface BudgetData {
  total: number;
  consomme: number;
  reste: number;
  pct: number;
  color: string;
  status: string;
}

export interface CoutItem {
  name: string;
  value: number;
  color: string;
  pct: number;
}

export interface ChartDataReturn {
  // Données du chantier
  chantierNom: string;
  chantierReference: string;

  // Données des graphiques
  evolutionData: EvolutionDataPoint[];
  rentabiliteData: RentabiliteData;
  budgetData: BudgetData;
  coutsData: CoutItem[];

  // États
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

// =============================================================================
// CONSTANTES - Mapping des types de frais vers les catégories du graphique
// =============================================================================

// Types considérés comme "Matériaux" dans le graphique
const TYPES_MATERIAUX = ['Achat d\'outillage', 'Consommables'];

// Type "Sous-traitant"
const TYPE_SOUS_TRAITANT = 'Sous-traitant';

// Types à exclure du graphique (pénalités de retard, coûts retard)
const TYPES_EXCLUS = ['Pénalité de retard', 'Coût retard', 'cout retard', 'retard'];

// =============================================================================
// Types internes pour les données brutes
// =============================================================================

interface RawChartData {
  chantier: any;
  snapshots: any[];
  metrics: any;
  fraisChantier: any[];
  facturesFournisseurs: any[];
}

// =============================================================================
// HOOK PRINCIPAL
// =============================================================================

export function useChartData(chantierId: string): ChartDataReturn {
  const { entrepriseId } = useSubscription();


  // -------------------------------------------------------------------------
  // Query : charger les 5 sources de données en parallèle
  // -------------------------------------------------------------------------
  const {
    data: rawData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<RawChartData>({
    queryKey: queryKeys.charts.chantier(entrepriseId!, chantierId),
    queryFn: async () => {
      const [chantierRes, snapshotsRes, metricsRes, fraisRes, facturesRes] = await Promise.all([
        // 1. Infos du chantier
        supabase
          .from('chantiers')
          .select('id, nom_chantier, reference_chantier, budget_ht, date_debut_prevue, date_fin_estimee')
          .eq('id', chantierId)
          .single(),

        // 2. Snapshots des 30 derniers jours
        supabase
          .from('chantier_snapshots')
          .select('d, marge_a_date, cout_main_oeuvre, couts_fixes, budget_ht')
          .eq('chantier_id', chantierId)
          .gte('d', subDays(new Date(), 30).toISOString().split('T')[0])
          .order('d', { ascending: true }),

        // 3. Métriques temps réel
        supabase
          .from('chantier_metrics_realtime')
          .select('metrics')
          .eq('chantier_id', chantierId)
          .single(),

        // 4. Frais chantier (pour ventilation par type)
        supabase
          .from('frais_chantier')
          .select('type_frais, montant_ht, montant_total')
          .eq('chantier_id', chantierId),

        // 5. Factures fournisseurs (considérées comme matériaux)
        supabase
          .from('factures_fournisseurs')
          .select('montant_ht, montant_ttc')
          .eq('chantier_id', chantierId),
      ]);

      if (chantierRes.error) {
        throw new Error('Chantier non trouvé');
      }

      // Loguer les erreurs non-bloquantes sur les snapshots
      if (snapshotsRes.error) {
        console.error('[useChartData] Erreur snapshots:', snapshotsRes.error.message);
      }

      return {
        chantier: chantierRes.data,
        snapshots: snapshotsRes.data || [],
        metrics: metricsRes.data?.metrics || null,
        fraisChantier: fraisRes.data || [],
        facturesFournisseurs: facturesRes.data || [],
      };
    },
    enabled: !!entrepriseId && !!chantierId,
    staleTime: 2 * 60 * 1000,
  });

  // ==========================================================================
  // CALCUL DES DONNÉES DÉRIVÉES (identiques à l'ancien code)
  // ==========================================================================

  const chantier = rawData?.chantier;
  const snapshots = rawData?.snapshots ?? [];
  const metrics = rawData?.metrics;
  const fraisChantier = rawData?.fraisChantier ?? [];
  const facturesFournisseurs = rawData?.facturesFournisseurs ?? [];

  // Calcul des dépenses totales (MO + coûts fixes)
  const depensesTotales = useMemo(() => {
    if (!metrics) return 0;
    return (
      (metrics.cout_main_oeuvre_reel || 0) +
      (metrics.couts_fixes_engages || 0) +
      (metrics.penalites_retard || 0)
    );
  }, [metrics]);

  // Données d'évolution (courbes)
  const evolutionData = useMemo((): EvolutionDataPoint[] => {
    if (!snapshots.length && !chantier) return [];

    // Si pas de snapshots mais métriques dispo → créer 2 points (début + aujourd'hui)
    // pour que le graphique affiche au moins une ligne (il faut >= 2 points)
    if (!snapshots.length && metrics && chantier) {
      const today = new Date();
      const dateDebut = chantier.date_debut_prevue
        ? parseISO(chantier.date_debut_prevue)
        : subDays(today, 7);

      return [
        {
          date: format(dateDebut, 'yyyy-MM-dd'),
          dateLabel: format(dateDebut, 'dd MMM', { locale: fr }),
          marge: chantier.budget_ht || 0,
          coutsCumules: 0,
          budget: chantier.budget_ht || 0,
        },
        {
          date: format(today, 'yyyy-MM-dd'),
          dateLabel: format(today, 'dd MMM', { locale: fr }),
          marge: metrics.marge_a_date || 0,
          coutsCumules: depensesTotales,
          budget: chantier.budget_ht || 0,
        },
      ];
    }

    return snapshots.map((s: any) => ({
      date: s.d,
      dateLabel: format(parseISO(s.d), 'dd MMM', { locale: fr }),
      marge: s.marge_a_date || 0,
      coutsCumules: (s.cout_main_oeuvre || 0) + (s.couts_fixes || 0),
      budget: s.budget_ht || chantier?.budget_ht || 0,
    }));
  }, [snapshots, metrics, chantier, depensesTotales]);

  // Données de rentabilité (jauge)
  const rentabiliteData = useMemo((): RentabiliteData => {
    // Rentabilité temps réel (à ce jour)
    const value = metrics?.profitability_pct || 0;
    // Rentabilité finale estimée
    const valueFinale = metrics?.marge_finale_pct || 0;
    const objectif = 18; // Objectif BTP standard

    // Détermine si le chantier a de l'activité (dépenses ou main d'œuvre)
    const mainOeuvre = metrics?.cout_main_oeuvre_reel || 0;
    const coutsFixes = metrics?.couts_fixes_engages || 0;
    const hasActivity = mainOeuvre > 0 || coutsFixes > 0;

    // Fonction pour obtenir le statut selon la valeur
    const getStatus = (v: number): string => {
      if (v >= SEUILS_MARGE.EXCELLENT) return 'Excellent';
      if (v >= SEUILS_MARGE.BON) return 'Bon';
      if (v >= SEUILS_MARGE.CORRECT) return 'À surveiller';
      if (v >= SEUILS_MARGE.FRAGILE) return 'Fragile';
      if (v >= SEUILS_MARGE.CRITIQUE) return 'Critique';
      return 'Déficit';
    };

    return {
      value,
      valueFinale,
      objectif,
      color: getMargeColor(value),
      colorFinale: getMargeColor(valueFinale),
      status: getStatus(value),
      statusFinale: getStatus(valueFinale),
      hasActivity,
    };
  }, [metrics]);

  // Données de budget (barre de progression)
  const budgetData = useMemo((): BudgetData => {
    // Prioriser metrics.budget_ht car il vient du devis actif (source de vérité)
    // chantier.budget_ht peut contenir le TTC par erreur
    const total = metrics?.budget_ht || chantier?.budget_ht || 0;
    const consomme = depensesTotales;
    const reste = total - consomme;
    const pct = total > 0 ? (consomme / total) * 100 : 0;

    let status = 'Confortable';
    if (pct >= 90) status = 'Critique';
    else if (pct >= 70) status = 'À surveiller';

    return {
      total,
      consomme,
      reste,
      pct,
      color: getBudgetColor(pct),
      status,
    };
  }, [chantier, metrics, depensesTotales]);

  // Données de répartition des coûts (donut) - VENTILATION DÉTAILLÉE PAR TYPE
  const coutsData = useMemo((): CoutItem[] => {
    const items: CoutItem[] = [];

    // 1. Main d'œuvre depuis les métriques
    const mo = metrics?.cout_main_oeuvre_reel || 0;
    if (mo > 0) {
      items.push({
        name: 'Main d\'œuvre',
        value: mo,
        color: CHART_STATUS_COLORS.mainOeuvre,
        pct: 0, // Calculé après
      });
    }

    // 2. Agréger les frais par type
    const fraisParType: Record<string, number> = {};

    fraisChantier.forEach((f: any) => {
      const montant = f.montant_ht ?? f.montant_total ?? 0;
      const typeFrais = f.type_frais || 'Autre';

      // Exclure les pénalités et coûts de retard
      const isExclu = TYPES_EXCLUS.some(t =>
        typeFrais.toLowerCase().includes(t.toLowerCase())
      );
      if (isExclu || montant <= 0) return;

      fraisParType[typeFrais] = (fraisParType[typeFrais] || 0) + montant;
    });

    // Ajouter chaque type de frais au graphique
    Object.entries(fraisParType).forEach(([typeFrais, montant]) => {
      items.push({
        name: typeFrais,
        value: montant,
        color: FRAIS_TYPE_COLORS[typeFrais] || CHART_STATUS_COLORS.autre,
        pct: 0, // Calculé après
      });
    });

    // 3. Ajouter les factures fournisseurs comme catégorie séparée
    const totalFournisseurs = facturesFournisseurs.reduce((sum: number, f: any) => {
      return sum + (f.montant_ht ?? f.montant_ttc ?? 0);
    }, 0);

    if (totalFournisseurs > 0) {
      items.push({
        name: 'Fournisseurs',
        value: totalFournisseurs,
        color: CHART_STATUS_COLORS.fournisseurs,
        pct: 0,
      });
    }

    // Calculer le total et les pourcentages
    const total = items.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return [];

    // Mettre à jour les pourcentages
    items.forEach(item => {
      item.pct = (item.value / total) * 100;
    });

    // Trier par valeur décroissante
    return items.sort((a, b) => b.value - a.value);
  }, [metrics, fraisChantier, facturesFournisseurs]);

  return {
    chantierNom: chantier?.nom_chantier || '',
    chantierReference: chantier?.reference_chantier || '',
    evolutionData,
    rentabiliteData,
    budgetData,
    coutsData,
    isLoading,
    error: queryError?.message ?? null,
    refetch: async () => { await refetch(); },
  };
}
